import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Edit2, Trash2, MessageSquareText, Save, X } from 'lucide-react';

/**
 * Manage the templated communications RTR sends for each appraisal status.
 * Three default recipients (client / tenant / agent) and three channels
 * (text / email / both). Authors use `{{ namespace.token }}` placeholders;
 * unknown tokens render as-is so the preview surfaces missing data.
 */

const STATUSES = ['Appraisal Booked', 'Accepted', 'Completed', 'Quote Sent', 'Work Booked', 'Invoiced'];
const RECIPIENTS = ['client', 'tenant', 'agent'];
const CHANNELS = ['text', 'email', 'both'];

const blankScript = () => ({ name: '', status: 'Appraisal Booked', recipient: 'client', channel: 'text', subject: '', body: '', active: true });

export default function CommunicationScriptsPage() {
  const { api } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filter, setFilter] = useState({ status: '', recipient: '' });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/communication-scripts');
      setScripts(Array.isArray(data) ? data : []);
    } catch { setScripts([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    const { _id, ...payload } = editing;
    try {
      if (_id) await api.put(`/communication-scripts/${_id}`, payload);
      else await api.post('/communication-scripts', payload);
      setEditing(null); load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/communication-scripts/${id}`); setConfirmDelete(null); load(); } catch {/* no-op */}
  };

  const visible = scripts.filter(s =>
    (!filter.status || s.status === filter.status) &&
    (!filter.recipient || s.recipient === filter.recipient)
  );

  // Group by status for an easier scan.
  const grouped = visible.reduce((acc, s) => {
    const key = s.status || '(no status)';
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  return (
    <div data-testid="communication-scripts-page" className="space-y-5">
      <div className="bg-white border border-[#E2E2DF] rounded-md p-6 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-[#D35400]" />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#D35400]/15 to-[#D35400]/5 flex items-center justify-center ring-1 ring-[#D35400]/10">
              <MessageSquareText size={22} className="text-[#D35400]" strokeWidth={2} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-0.5">Settings</p>
              <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope] tracking-tight">Communication Scripts</h1>
              <p className="text-sm text-slate-500 mt-1">{scripts.length} script{scripts.length === 1 ? '' : 's'} — templates sent automatically as an appraisal moves through each status.</p>
            </div>
          </div>
          <button data-testid="new-script-btn" onClick={() => setEditing(blankScript())} className="flex items-center gap-2 px-3.5 py-2 bg-[#D35400] text-white rounded-md text-sm font-semibold hover:bg-[#A04000]">
            <Plus size={14} strokeWidth={2.5} /> New Script
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E2E2DF] rounded-xl p-4 flex flex-wrap gap-3 items-center">
        <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Filter:</span>
        <select data-testid="filter-status" value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })} className="px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm">
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="filter-recipient" value={filter.recipient} onChange={e => setFilter({ ...filter, recipient: e.target.value })} className="px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm">
          <option value="">All recipients</option>
          {RECIPIENTS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {/* Scripts grouped by status */}
      {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([status, list]) => (
        <div key={status} className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-[#E2E2DF]">
            <h2 className="text-sm font-bold text-slate-800">{status}</h2>
          </div>
          <div className="divide-y divide-[#F0EFEC]">
            {list.map(s => (
              <div key={s._id} data-testid={`script-row-${s._id}`} className="p-5 hover:bg-slate-50/50 group">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800">{s.name || '(unnamed)'}</h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                      <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 font-semibold capitalize">{s.recipient}</span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 font-semibold capitalize">{s.channel}</span>
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button data-testid={`edit-script-${s._id}`} onClick={() => setEditing({ ...s })} className="p-1.5 text-slate-400 hover:text-[#D35400]"><Edit2 size={14} /></button>
                    {confirmDelete === s._id ? (
                      <>
                        <button data-testid={`confirm-delete-script-${s._id}`} onClick={() => remove(s._id)} className="px-2 py-0.5 bg-red-500 text-white rounded text-xs">Delete</button>
                        <button onClick={() => setConfirmDelete(null)} className="px-2 py-0.5 border border-slate-300 rounded text-xs text-slate-500">Cancel</button>
                      </>
                    ) : (
                      <button data-testid={`delete-script-${s._id}`} onClick={() => setConfirmDelete(s._id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
                {s.subject && <p className="text-xs font-semibold text-slate-600 mb-1">Subject: {s.subject}</p>}
                <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans bg-slate-50/60 border border-[#E2E2DF] rounded-md p-2.5 leading-relaxed line-clamp-5">{s.body}</pre>
              </div>
            ))}
          </div>
        </div>
      ))}

      {Object.keys(grouped).length === 0 && (
        <div className="bg-white border border-[#E2E2DF] rounded-xl p-12 text-center">
          <MessageSquareText size={28} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No scripts match the current filter. Adjust the filters above or add a new script.</p>
        </div>
      )}

      {/* Token reference */}
      <div className="bg-blue-50/40 border border-blue-100 rounded-lg p-4">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-blue-700 mb-2">Available tokens</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono-num text-blue-900">
          {[
            'client.first_name', 'client.name', 'client.email', 'client.phone',
            'property.address', 'property.full_address', 'property.suburb', 'property.city',
            'tenant.name', 'tenant.phone', 'tenant.email',
            'agent.name', 'agent.company', 'agent.phone', 'agent.email',
            'appraisal.no', 'appraisal.status', 'appraisal.booked_date', 'appraisal.booked_time',
            'appraisal.appraiser', 'appraisal.contract_amount', 'appraisal.completed_date',
            'company.name', 'company.phone', 'company.email',
          ].map(t => <span key={t} className="px-1.5 py-0.5 bg-white border border-blue-200 rounded">{`{{ ${t} }}`}</span>)}
        </div>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div data-testid="script-editor-modal" className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-base font-bold text-[#0F172A]">{editing._id ? 'Edit script' : 'New script'}</h2>
              <button onClick={() => setEditing(null)} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Name *</label>
                <input data-testid="script-name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Status</label>
                  <select data-testid="script-status" value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm bg-white">
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Recipient</label>
                  <select data-testid="script-recipient" value={editing.recipient} onChange={e => setEditing({ ...editing, recipient: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm bg-white">
                    {RECIPIENTS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Channel</label>
                  <select data-testid="script-channel" value={editing.channel} onChange={e => setEditing({ ...editing, channel: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm bg-white">
                    {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Subject (email only)</label>
                <input data-testid="script-subject" value={editing.subject} onChange={e => setEditing({ ...editing, subject: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" placeholder="e.g. Roof appraisal booked for {{property.address}}" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Body *</label>
                <textarea data-testid="script-body" value={editing.body} onChange={e => setEditing({ ...editing, body: e.target.value })} rows={10} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm font-mono-num leading-relaxed" />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50/50">
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-white font-medium">Cancel</button>
              <button data-testid="script-save-btn" onClick={save} className="px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-semibold hover:bg-[#A04000] flex items-center gap-1.5"><Save size={14} /> Save script</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
